<template>
  <ExpansionCard
    :columnCollapsed="columnCollapsed"
    :overflow="entries.length < 2"
    :collapse="collapse"
    :backgroundColor="backgroundColor"
    :dividerStyles="dividerStyles"
    :toggleExpandButtonStyles="toggleExpandButtonStyles"
    :titleAsCollapseToggle="titleAsCollapseToggle"
    :titleStyles="titleStyles"
  >
    <template #title="{ collapsed }">
      <div class="truncate text-lg font-bold md:px-1 lg:text-6.5pt">
        <slot name="title" :collapsed="collapsed" />
      </div>
    </template>
    <template #action>
      <slot name="action" />
    </template>
    <template #subtitle="{ collapsed }">
      <div class="truncate text-5pt md:px-1">
        <slot name="subtitle" :collapsed="collapsed" />
      </div>
    </template>
    <template #default>
      <div
        class="flex h-full flex-col"
        :class="{ 'min-h-20': entries.length > 1 }"
      >
        <div v-if="loading" class="flex h-full items-center justify-center">
          <i-line-md-loading-twotone-loop class="h-8 w-8" />
        </div>
        <template v-else>
          <div
            v-for="(list, listIndex) in entries"
            :key="listIndex"
            class="flex min-h-0 flex-col"
            :class="{
              'mt-2': listIndex > 0,
              'flex-shrink-0': list.length === 0,
              'flex-auto': list.length > 0,
            }"
          >
            <span class="shrink-0 truncate pl-0 text-5pt empty:p-0 lg:pl-2">
              <slot :name="'list-header-' + listIndex.toString()" />
            </span>
            <div
              v-if="listIndex > 0"
              class="mx-1 mb-1 border-b-2 border-lexmea-gray-500"
            ></div>
            <ul
              v-if="list.length"
              :key="listIndex"
              class="list-none overflow-y-auto pb-1"
            >
              <li
                v-for="(entry, i) in list"
                :key="entry.id"
                :ref="(el) => pushOrderedRef(el as HTMLLIElement | null, entry)"
                class="mb-1 flex cursor-pointer items-center overflow-hidden transition hover:text-lexmea-pop-lightest"
                :class="{
                  'border-t border-dashed border-t-gray-400':
                    dividingEntryIndex === i && i > 0,
                }"
                @mouseover="hoveredItem = entry.id"
                @mouseleave="hoveredItem = null"
              >
                <slot
                  name="item"
                  :entry="entry"
                  :index="i"
                  :listIndex="listIndex"
                />
                <Transition name="slide-x">
                  <button
                    v-if="entry.editable && hoveredItem === entry.id"
                    class="fill-current text-gray-400 transition hover:text-gray-800"
                    @click="$emit('delete', entry)"
                  >
                    <i-heroicons-trash class="mx-2 h-5 w-5" />
                  </button>
                </Transition>
              </li>
            </ul>
            <div
              v-else
              :key="'empty-' + listIndex.toString()"
              class="flex-shrink-0 truncate text-5pt italic lg:px-2"
            >
              <slot :name="'empty-' + listIndex.toString()" />
            </div>
          </div>
        </template>
      </div>
    </template>
  </ExpansionCard>
</template>

<script lang="ts" setup generic="T extends IUserEntry">
import type { IUserEntry } from "~/models/IUserEntry";

const props = withDefaults(
  defineProps<{
    entries: T[][];
    columnCollapsed: boolean;
    dividingEntryIndex?: number;
    loading?: boolean;
    collapse?: boolean;
    backgroundColor?: "grey" | "transparent";
    titleAsCollapseToggle?: boolean;
    toggleExpandButtonStyles?: string;
    dividerStyles?: string;
    titleStyles?: string;
  }>(),
  {
    backgroundColor: "grey",
    dividingEntryIndex: 0,
    titleAsCollapseToggle: false,
    toggleExpandButtonStyles: undefined,
    dividerStyles: undefined,
    titleStyles: undefined,
  }
);

defineEmits<{
  delete: [entry: T];
}>();

const entries = toRef(props, "entries");

const hoveredItem = ref<number | null>(null);

const listRefs = ref<[IUserEntry, HTMLLIElement][]>([]);
const isMounted = useMounted();

const { active } = useFetchActiveEntry();
const { isActive } = useActiveEntryRouteParams();

/**
 * Pushes the template ref of the list item to the `listRefs` array,
 * while making sure that the index matches the index of the entry in the `entries` array.
 */
const pushOrderedRef = (el: HTMLLIElement | null, entry: IUserEntry) => {
  if (el) {
    listRefs.value.push([entry, el]);
  }
};

onUpdated(() => {
  if (isMounted) {
    listRefs.value = [];
  }
});

watch([entries, active], () => {
  // we can only access the template refs `listRefs` after the component mounted
  if (
    isMounted &&
    entries.value.every((list) => list.every((e) => e.type === "schema"))
  ) {
    const activeEntries = listRefs.value.filter(([entry]) => isActive(entry));

    activeEntries.forEach(([, el]) => {
      el.scrollIntoView({
        behavior: "smooth",
        block: "center",
        inline: "start",
      });
    });
  }
});
</script>
